import { useState, useEffect } from "react";
import {
  Box,
  Button,
  chakra,
  Flex,
  Text,
  useMediaQuery,
  useToast,
  Tooltip,
} from "@chakra-ui/react";
import NextImage from "next/legacy/image";
import { useRouter } from "next/router";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { IoIosArrowDropdown, IoIosArrowDropright } from "react-icons/io";

import {
  CREDITS_PER_ESSAY,
  CREDITS_PER_PROFILE,
  PROFILE_PLANS,
  ESSAY_PLANS,
} from "@/utils/data/products";
import { useAPI } from "@/contexts/APIContext";
import { useUserData } from "@/contexts/UserDataContext";
import ModalAuth from "@/components/user/ModalAuth";
import ModalCollegeError from "./ModalCollegeError";
import FreeCreditsModal from "./FreeCreditsModal";
import useClaimFreeCredits from "./useClaimFreeCredits";
import { Total } from "./Total";
import { purchaseAnalytics } from "@/utils/analytics";
import { getDiscountData } from "@/utils/discount-service";
import Link from "next/link";
import useTotalCheckout from "@/hooks/useTotalCheckout";

const packagesWhatIGet = [
  "Personal Statement",
  "Supplemental Essays",
  "Short responses",
  "SAT, ACT Test Scores",
  "Academic Transcript",
  "Extracurricular activities",
  "Honors & Awards",
  "Scholarships",
  "Q&A and additional info",
];

const essaysWhatIGet = [
  "Central Theme Validation",
  "Writing Basics Assessment",
  "Admissions Rubric Assessment",
  "Impression Analysis",
  "Grammarly Suggestions",
  "Strength-based Feedback",
  "Constructive Criticism",
  "Personalized recommendations",
  "Next Steps",
];

export const Packages = ({
  selectedProfilePlan,
  setSelectedProfilePlan,
  selectedEssayPlan,
  setSelectedEssayPlan,
}) => {
  const router = useRouter();
  const toast = useToast();
  const api = useAPI();

  const { currentUser, userData } = useUserData();

  const { claimedFreeSuccess, setClaimedFreeSuccess, claimFreeCredits, loadingFree } =
    useClaimFreeCredits();

  const [isMobile] = useMediaQuery("(max-width: 768px)");

  const { finalPrice } = useTotalCheckout({
    profilePlan: selectedProfilePlan,
    revisionPlan: selectedEssayPlan,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [collegeErrorVisible, setCollegeErrorVisible] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const { discountCode } = getDiscountData();

  useEffect(() => {
    if (
      // Check if we are not on the checkout modal
      !router.query.clientSecret &&
      (!!router.query.profiles_plan || !!router.query.essays_plan)
    ) {
      setSelectedProfilePlan(PROFILE_PLANS.find((plan) => plan.sku === router.query.profiles_plan));
      setSelectedEssayPlan(ESSAY_PLANS.find((plan) => plan.sku === router.query.essays_plan));

      onCheckout();
    }
  }, [router, setSelectedProfilePlan, setSelectedEssayPlan]);

  const onSelectProfilePlan = (plan) => {
    if (plan.id === selectedProfilePlan?.id) {
      setSelectedProfilePlan(null);
    } else {
      setSelectedProfilePlan(plan);
    }
  };

  const onSelectEssayPlan = (plan) => {
    if (plan.id === selectedEssayPlan?.id) {
      setSelectedEssayPlan(null);
    } else {
      setSelectedEssayPlan(plan);
    }
  };

  const handleSamplePurchase = async () => {
    if (userData?.claimedFreeUnlock) {
      return toast({
        title: "Cannot claim credits!",
        description: "You have already claimed free credits!",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }

    if (currentUser) {
      if (userData.type !== "college") {
        return await claimFreeCredits();
      }

      return setCollegeErrorVisible(true);
    } else {
      setShowAuthModal(true);
    }
  };

  const redirect = async () => {
    setIsLoading(true);

    const profiles_plan = selectedProfilePlan?.sku;
    const essays_plan = selectedEssayPlan?.sku;

    const response = await api.post("/stripe/payment-intent", true, {
      profiles_plan,
      essays_plan,
      discountCode: discountCode || null,
    });

    if (!!response) {
      const { clientSecret } = response;

      const query = { ...router.query, clientSecret };

      if (profiles_plan && !router.query.profiles_plan) {
        query.profiles_plan = profiles_plan;
      }

      if (essays_plan && !router.query.essays_plan) {
        query.essays_plan = essays_plan;
      }

      router.push({
        ...router,
        query,
      });

      purchaseAnalytics.onCheckoutShowPaymentModal();
    } else {
      // There was an error
      toast({
        title: "Error",
        description: `There was an error processing the request. Server returned: ${response}`,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    }

    setIsLoading(false);
  };

  const onCheckout = async () => {
    if (currentUser && userData.type === "college") {
      setCollegeErrorVisible(true);
      return;
    }

    purchaseAnalytics.onCheckoutClickBuy({ value: finalPrice });

    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    setIsLoading(true);
    return await redirect();
  };

  return (
    <Box border="1px solid black" borderRadius="14px" py={3} px={{ base: 2, md: 5 }} bg="#fff">
      <Box minW={{ base: "100%", md: 550 }}>
        <Flex gap={{ base: 2, md: 4 }}>
          <ProfilesHeader />
          <EssaysHeader />
        </Flex>

        {isMobile && (
          <Flex gap={{ base: 2, md: 4 }} mt={4}>
            <WhatDoIGet list={packagesWhatIGet} />
            <WhatDoIGet list={essaysWhatIGet} />
          </Flex>
        )}
      </Box>

      <Flex gap={{ base: 2, md: 4 }}>
        <Flex flex="1" gap={2} mt={6}>
          {!isMobile && <WhatDoIGet list={packagesWhatIGet} />}

          <ProfilesColumn
            selectedProfilePlan={selectedProfilePlan}
            onSelectProfilePlan={onSelectProfilePlan}
          />
        </Flex>

        <Flex flex="1" gap={2} mt={6}>
          {!isMobile && <WhatDoIGet list={essaysWhatIGet} />}

          <EssaysColumn
            selectedEssayPlan={selectedEssayPlan}
            onSelectEssayPlan={onSelectEssayPlan}
          />
        </Flex>
      </Flex>

      <Flex gap={{ base: 2, md: 4 }} mt={4}>
        <Button
          height="auto"
          whiteSpace="normal"
          rightIcon={<ArrowForwardIcon />}
          alignSelf="flex-start"
          variant="ghost"
          isLoading={loadingFree}
          onClick={handleSamplePurchase}
          py={1}
          flex={1}
        >
          CLAIM 1 FREE PROFILE
        </Button>

        <Link style={{ flex: 1 }} href="https://admityogi.com/blog/admityogi-essays-in-action">
          <Button
            height="auto"
            whiteSpace="normal"
            rightIcon={<ArrowForwardIcon />}
            alignSelf="flex-start"
            variant="ghost"
            py={1}
          >
            READ SAMPLE REVISION
          </Button>
        </Link>
      </Flex>

      <Total
        profilePlan={selectedProfilePlan}
        revisionPlan={selectedEssayPlan}
        onCheckout={onCheckout}
        isLoading={isLoading}
      />

      <ModalCollegeError open={collegeErrorVisible} onClose={() => setCollegeErrorVisible(false)} />

      <FreeCreditsModal isOpen={claimedFreeSuccess} onClose={() => setClaimedFreeSuccess(false)} />

      <ModalAuth
        header="To purchase this package, create an account with us."
        open={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onComplete={() => {
          const query = { ...router.query };

          if (selectedProfilePlan) {
            query.profiles_plan = selectedProfilePlan.sku;
          }

          if (setSelectedEssayPlan) {
            query.essays_plan = selectedEssayPlan.sku;
          }

          router.push({
            ...router,
            query,
          });
        }}
      />
    </Box>
  );
};

const ProfilesColumn = ({ selectedProfilePlan, onSelectProfilePlan }) => {
  return (
    <Flex direction="column" gap={2} mt={1} flexBasis={{ base: "100%", md: "140px" }}>
      {PROFILE_PLANS.map((plan) => {
        return (
          <Flex
            key={plan.id}
            flex={1}
            bg="#DFEFFD"
            borderRadius="10px"
            flexDirection="column"
            align="center"
            justifyContent="center"
            px={1}
            py={2}
            cursor="pointer"
            border={
              selectedProfilePlan?.id === plan.id ? "2px solid #000" : "2px solid transparent"
            }
            onClick={() => {
              onSelectProfilePlan(plan);
            }}
            pos="relative"
          >
            <Flex>
              <NextImage src="/images/icons/credits-icon.svg" width="20px" height="20px" />
              <Text fontWeight={600}>{plan.credits}</Text>

              {plan.isMostPopular && (
                <Box pos="absolute" top="0" right="2">
                  <Tooltip label="Most Popular" fontSize="sm" placement="top">
                    <Text fontSize="lg">💥</Text>
                  </Tooltip>
                </Box>
              )}
            </Flex>

            <Text fontSize={"12px"}>{Math.round(plan.credits / CREDITS_PER_PROFILE)} profiles</Text>
          </Flex>
        );
      })}
    </Flex>
  );
};

const EssaysColumn = ({ selectedEssayPlan, onSelectEssayPlan }) => {
  return (
    <Flex direction="column" gap={2} mt={1} flexBasis={{ base: "100%", md: "140px" }}>
      {ESSAY_PLANS.map((plan) => {
        return (
          <Flex
            key={plan.id}
            flex={1}
            bg="#DFEFFD"
            borderRadius="10px"
            flexDirection="column"
            align="center"
            justifyContent="center"
            px={1}
            py={2}
            cursor="pointer"
            border={selectedEssayPlan?.id === plan.id ? "2px solid #000" : "2px solid transparent"}
            onClick={() => {
              onSelectEssayPlan(plan);
            }}
            pos="relative"
          >
            {!plan.period ? (
              <>
                <Flex>
                  <NextImage src="/images/icons/credits-icon.svg" width="20px" height="20px" />
                  <Text fontWeight={600}>{plan.credits}</Text>
                </Flex>

                <Text fontSize={"12px"}>
                  {Math.round(plan.credits / CREDITS_PER_ESSAY)} revisions
                </Text>

                {plan.isMostPopular && (
                  <Box pos="absolute" top="0" right="2">
                    <Tooltip label="Most Popular" fontSize="sm" placement="top">
                      <Text fontSize="lg">💥</Text>
                    </Tooltip>
                  </Box>
                )}
              </>
            ) : (
              <>
                <Flex>
                  <NextImage src="/images/icons/essay-credits.svg" width="30px" height="30px" />
                  <Text fontWeight={600} lineHeight="16px">
                    {plan.title}
                  </Text>
                </Flex>

                <Text fontSize="12px">worth {plan.initialPrice} Credits</Text>

                {plan.isMostPopular && (
                  <Box pos="absolute" top="0" right="2">
                    <Tooltip label="Most Popular" fontSize="sm" placement="top">
                      <Text fontSize="lg">💥</Text>
                    </Tooltip>
                  </Box>
                )}
              </>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
};

const ProfilesHeader = () => {
  return (
    <Flex
      flex={1}
      p={2}
      border="1px solid black"
      borderRadius="10px"
      boxShadow="-5px 5px 0px 0px #DBEDFD"
      align="center"
      gap={1}
      justify="space-between"
      flexDirection={{ base: "column", md: "row" }}
    >
      <Flex align="center" gap={1} p={1}>
        <Text fontSize="3xl">👩‍🎓</Text>

        <Flex direction="column" mt={2}>
          <Text fontSize="16px" fontWeight={600} lineHeight="10px">
            Admityogi
          </Text>

          <Text color="#EF38AA" fontWeight={600}>
            Profiles
          </Text>
        </Flex>
      </Flex>

      <Flex align="center">
        <Text>1 profile =</Text>

        <Flex align="center">
          <NextImage src="/images/icons/credits-icon.svg" width="30px" height="30px" />

          <p>{CREDITS_PER_PROFILE}</p>
        </Flex>
      </Flex>
    </Flex>
  );
};

const EssaysHeader = () => {
  return (
    <Flex
      flex={1}
      p={2}
      border="1px solid black"
      borderRadius="10px"
      boxShadow="-5px 5px 0px 0px #DBEDFD"
      align="center"
      gap={1}
      justify="space-between"
      flexDirection={{ base: "column", md: "row" }}
    >
      <Flex align="center" gap={1} p={1}>
        <Text fontSize="3xl">📝</Text>

        <Flex direction="column" mt={2}>
          <Text fontSize="16px" fontWeight={600} lineHeight="10px">
            Admityogi
          </Text>

          <Text color="#60B0F7" fontWeight={600}>
            Essays
          </Text>
        </Flex>
      </Flex>

      <Flex align="center">
        <Text>1 revision =</Text>

        <Flex align="center">
          <NextImage src="/images/icons/credits-icon.svg" width="30px" height="30px" />

          <p>{CREDITS_PER_ESSAY}</p>
        </Flex>
      </Flex>
    </Flex>
  );
};

const WhatDoIGet = ({ list }) => {
  const [isMobile] = useMediaQuery("(max-width: 768px)");

  const [isOpen, setIsOpen] = useState(false);

  if (isMobile) {
    return (
      <Box flex="1">
        <Flex gap={2} alignItems="center" onClick={() => setIsOpen((prev) => !prev)}>
          <Text fontSize={{ base: "16px", md: "20px" }} fontWeight={{ base: 400, md: 700 }}>
            What do I get?
          </Text>

          {isOpen ? <IoIosArrowDropdown fill="#60B0F7" /> : <IoIosArrowDropright fill="#60B0F7" />}
        </Flex>

        {isOpen && (
          <chakra.ul fontSize={{ base: "12px", md: "14px" }} ml={4}>
            {list.map((text) => (
              <chakra.li key={text}>{text}</chakra.li>
            ))}
          </chakra.ul>
        )}
      </Box>
    );
  }

  return (
    <Box flex="1">
      <Flex gap={2} alignItems="center" onClick={() => setIsOpen((prev) => !prev)}>
        <Text fontSize={{ base: "16px", md: "20px" }} fontWeight={{ base: 400, md: 700 }}>
          What do I get?
        </Text>
      </Flex>

      <chakra.ul fontSize={{ base: "12px", md: "14px" }} ml={4}>
        {list.map((text) => (
          <chakra.li key={text}>{text}</chakra.li>
        ))}
      </chakra.ul>
    </Box>
  );
};
